import { useEffect } from 'react';
import toast from 'react-hot-toast';
import StatsWrapper from '../../utils/StatsWrapper';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import {
  getPendingDealClick,
  clearPendingDealClick,
  isDealFollowupHandled,
  markDealFollowupHandled,
  isDealFollowupCooldownElapsed,
  markDealFollowupShownNow,
} from '../../utils/dealFollowupStorage';
import DealFollowupToastContent from './DealFollowupToastContent';

// Must match Botimza/server/services/featureFlags/flagConsts.js DEAL_CLICK_FOLLOWUP_POPUP_FLAG -
// the frontend never imports server-side files, so flag names are matched by literal string
// (same convention as every other flag read via window.featureFlags elsewhere in this app).
const DEAL_CLICK_FOLLOWUP_POPUP_FLAG = 'featureFlags.deal.clickFollowupPopup';

// Minimum time since the click before we'll show the popup on tab-focus - avoids firing on an
// instant/accidental re-focus before the visitor could have looked at anything.
const MIN_DWELL_MS = 8000;
// Minimum gap between two shown popups in the same session - protects rapid multi-tab browsing
// from being interrupted repeatedly (docs/deal-signal-design.md §6).
const COOLDOWN_MS = 10 * 60 * 1000;

/**
 * Global, invisible listener for the post-click-out follow-up popup
 * (docs/deal-signal-design.md §6). Mount once, near the app root (Layout.jsx) -
 * it renders nothing itself, it just arms a `visibilitychange` listener and
 * spawns a react-hot-toast when the conditions are right.
 */
function DealFollowupPopup() {
  // The flag is only ever true for a logged-in account matched by the qualifier
  // (currently: the admin's own trackerId, for dogfooding) - see
  // docs/deal-signal-design.md §6 "Rollout: feature flag, scoped to one account first".
  const enabled = useFeatureFlag(DEAL_CLICK_FOLLOWUP_POPUP_FLAG);

  useEffect(() => {
    if (!enabled) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;

      const pending = getPendingDealClick();
      if (!pending?.asin) return;

      if (Date.now() - pending.clickedAt < MIN_DWELL_MS) return; // too soon, try again next focus
      if (isDealFollowupHandled(pending.asin)) {
        clearPendingDealClick();
        return;
      }
      if (!isDealFollowupCooldownElapsed(COOLDOWN_MS)) return;

      // Commit to showing it now - mark handled/cooldown immediately so a rapid
      // double-fire of visibilitychange can't show it twice.
      markDealFollowupHandled(pending.asin);
      markDealFollowupShownNow();
      clearPendingDealClick();
      StatsWrapper.track('deal.followup_shown', { asin: pending.asin });

      toast.custom(
        (t) => (
          <DealFollowupToastContent
            toastId={t.id}
            asin={pending.asin}
            itemName={pending.itemName}
            domain={pending.domain}
            href={pending.href}
          />
        ),
        // position is irrelevant to where this actually renders - DealFollowupToastContent
        // overrides it with its own fixed, viewport-centered overlay (react-hot-toast has no
        // true center option). Kept as 'bottom-center' only because it's the neutral default
        // for react-hot-toast's internal stacking/offset math.
        { duration: Infinity, position: 'bottom-center' }
      );
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [enabled]);

  return null;
}

export default DealFollowupPopup;
